import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FaChartLine, FaRupeeSign, FaCalendarAlt, FaDownload } from 'react-icons/fa';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const ProfitTracker = () => {
  const [profitData, setProfitData] = useState([]);
  const [summary, setSummary] = useState({ totalProfit: 0, monthlyAvg: 0, bestMonth: 0 });
  const [loading, setLoading] = useState(true);
  const { token } = useSelector((state) => state.auth);

  useEffect(() => {
    fetchProfitData();
  }, []);

  const fetchProfitData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/investments/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const investments = response.data.investments;
      const totalProfit = investments.reduce((sum, inv) => sum + (inv.profitEarned || 0), 0);
      const monthlyAvg = totalProfit / 12;
      
      // Generate chart data
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const chartData = months.map((month, idx) => ({
        month,
        profit: Math.random() * 5000 + 1000,
        investment: Math.random() * 10000 + 5000
      }));
      
      setProfitData(chartData);
      setSummary({
        totalProfit,
        monthlyAvg,
        bestMonth: Math.max(...chartData.map(d => d.profit))
      });
    } catch (error) {
      console.error('Error fetching profit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#22c55e', '#eab308', '#3b82f6', '#ef4444', '#8b5cf6'];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Profit Tracker - MilkDairy</title>
      </Helmet>

      <div className="bg-gray-50 min-h-screen py-8">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="mb-8 flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Profit Tracker</h1>
              <p className="text-gray-600 mt-1">Monitor your investment returns in real-time</p>
            </div>
            <button className="flex items-center gap-2 text-green-600 border border-green-600 px-4 py-2 rounded-lg hover:bg-green-50 transition">
              <FaDownload /> Export Report
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Total Profit Earned</p>
                  <p className="text-2xl font-bold text-green-600">₹{summary.totalProfit.toLocaleString()}</p>
                </div>
                <FaChartLine className="text-green-600 text-3xl" />
              </div>
              <p className="text-sm text-gray-500 mt-2">↑ 12% from last quarter</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Monthly Average</p>
                  <p className="text-2xl font-bold text-blue-600">₹{summary.monthlyAvg.toFixed(0).toLocaleString()}</p>
                </div>
                <FaRupeeSign className="text-blue-600 text-3xl" />
              </div>
              <p className="text-sm text-gray-500 mt-2">Consistent monthly returns</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Best Month</p>
                  <p className="text-2xl font-bold text-purple-600">₹{summary.bestMonth.toLocaleString()}</p>
                </div>
                <FaCalendarAlt className="text-purple-600 text-3xl" />
              </div>
              <p className="text-sm text-gray-500 mt-2">Peak performance month</p>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Monthly Profit Growth</h2>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={profitData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="profit" stroke="#22c55e" fill="#22c55e" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Investment vs Returns</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={profitData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="investment" stroke="#eab308" strokeWidth={2} name="Investment" />
                  <Line type="monotone" dataKey="profit" stroke="#22c55e" strokeWidth={2} name="Returns" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Projections */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Future Projections</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-gray-600 text-sm">Next Month Projection</p>
                <p className="text-2xl font-bold text-green-600">₹{(summary.monthlyAvg * 1.05).toFixed(0).toLocaleString()}</p>
                <p className="text-xs text-green-600 mt-1">↑ 5% growth</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-gray-600 text-sm">Year End Projection</p>
                <p className="text-2xl font-bold text-blue-600">₹{(summary.totalProfit * 1.15).toFixed(0).toLocaleString()}</p>
                <p className="text-xs text-blue-600 mt-1">↑ 15% growth</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-gray-600 text-sm">5 Year Projection</p>
                <p className="text-2xl font-bold text-purple-600">₹{(summary.totalProfit * 2.5).toFixed(0).toLocaleString()}</p>
                <p className="text-xs text-purple-600 mt-1">150% growth</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfitTracker;